import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { MatCheckboxModule } from "@angular/material/checkbox";
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { ColDef } from "ag-grid-community";
import { cloneDeep, flatten } from "lodash";
import { Subject } from "rxjs";
import { LevelModalComponent } from "../../../features/corporation-management/level-modal/level-modal.component";
import { AppConstants } from "../../constants/app-constants";
import { ClientSideListBaseComponent } from "../../core/client-side-list-base/client-side-list-base.component";
import { LevelMode } from "../../enums/level-mode.enum";
import { ButtonComponent } from "../core/button/button.component";
import { rowDragText } from "./hierarchy.config";
import {
  Hierarchy,
  HierarchyDataItem,
  HierarchyLevel,
} from "./hierarchy.interface";
import { MatTooltipModule } from "@angular/material/tooltip";

@Component({
  selector: "thera-hierarchy",
  standalone: true,
  styleUrls: ["./hierarchy.component.scss"],
  templateUrl: "./hierarchy.component.html",
  imports: [
    MatExpansionModule,
    CommonModule,
    ClientSideListBaseComponent,
    MatCheckboxModule,
    ButtonComponent,
    MatDialogModule,
    MatTooltipModule,
  ],
})
export class HierarchyComponent implements OnInit {
  unmappedChecked = false;
  _hierarchyData!: Hierarchy;
  rowDragText = rowDragText;
  @Input() emitOnChange = false;
  @Input() tableSelect: boolean = false;
  @Input() addLevel$!: Subject<any>;
  @Input() corpId!: string;
  @Input()
  set hierarchyData(data: Hierarchy) {
    this._hierarchyData = cloneDeep(data);
    this.onHierarchyDataChange();
  }

  get hierarchyData(): Hierarchy {
    return this._hierarchyData;
  }
  @Input() draggable: boolean = false;
  @Input() selectable: boolean = true;
  @Input() showAddLocationsButton = false;
  @Input() rowSelection: "single" | "multiple" | undefined;

  @Input() columnDefs!: ColDef[];
  @Output() facilitiesSelected: EventEmitter<string[]> = new EventEmitter();
  includedFacilities: any[] = [];
  @Output() hierarchyUpdated = new EventEmitter();
  @Output() facilityMoved = new EventEmitter();
  @Output() allSelectedFacilityIds: EventEmitter<string[]> = new EventEmitter();
  @Input() selectedFacilityIds: string[] | null = null;
  @Input() disabled: boolean = false;
  listCheckedFacilities: boolean = false;

  constructor(private dialog: MatDialog) { }

  ngOnInit(): void {
    if (this.selectable && this.selectedFacilityIds != null) {
      this.listCheckedFacilities = true;
      this.updateCheckedStatus(this.selectedFacilityIds, this.hierarchyData)
    }

    if (this.addLevel$) {
      this.addLevel$.subscribe(() => this.onAddLevel(null));
    }
  }

  updateCheckedStatus(facilityIds: string[], hierarchy: Hierarchy): void {
    let isAllFacilitiesChecked = true;
    hierarchy.unmappeditems.forEach(item => {
      item.checked = facilityIds.includes(item.id);
      if (!item.checked) {
        isAllFacilitiesChecked = false;
      }
    });
    this.unmappedChecked = isAllFacilitiesChecked;
    this.updateHierarchyLevel(hierarchy.hierarchies, facilityIds);
  }

  private updateHierarchyLevel(levels: HierarchyLevel[], facilityIds: string[]): void {
    levels.forEach(level => {
      level.items.forEach(item => {
        item.checked = facilityIds.includes(item.id);
      });

      if (level.levels && level.levels.length > 0) {
        this.updateHierarchyLevel(level.levels, facilityIds);
      }

      const allItemsChecked = level.items.every(item => item.checked);
      const allSubLevelsChecked = level.levels.every(sub => sub.checked);
      if(level.items.length > 0 || level.levels.length > 0)
          level.checked = allItemsChecked && allSubLevelsChecked;
    });
  }

  private onHierarchyDataChange(): void {
    if (this.listCheckedFacilities && this.selectedFacilityIds)
    {
      this.updateCheckedStatus(this.selectedFacilityIds, this.hierarchyData)
    }
  }

  onCheck(item: any, newCheckedValue: boolean) {
    this.walkAndCheck(item, newCheckedValue);
  }

  onLevelCheck(event: MouseEvent, level: any) {
  event.stopPropagation();
  this.onCheck(level, !level?.checked);
}

  onAddLocations() {
    this.facilitiesSelected.emit(this.includedFacilities);
    const filterCheckedItems = (hierarchy: HierarchyLevel) => {
      if (hierarchy.items?.length) {
        hierarchy.items = hierarchy.items.filter((item) => !item.checked);
      }

      if (hierarchy.levels?.length) {
        hierarchy.levels.forEach((level) => filterCheckedItems(level));
      }
    };

    const processHierarchies = (hierarchyData: Hierarchy) => {
      hierarchyData.hierarchies.forEach((hierarchy) => {
        filterCheckedItems(hierarchy);
      });
    };

    processHierarchies(this.hierarchyData);

    this.includedFacilities = [];
  }

  private updateSelectedFacilityId(facilityId: string, newCheckedValue: boolean) {
    if (!this.selectedFacilityIds) return;
    if (newCheckedValue) {
      if (!this.selectedFacilityIds.includes(facilityId)) {
        this.selectedFacilityIds.push(facilityId);
      }
    }
    else {
      this.selectedFacilityIds = this.selectedFacilityIds.filter(
        (x) => x !== facilityId
      );
    }
    this.allSelectedFacilityIds.emit(this.selectedFacilityIds);
  }

  private walkAndCheck(
    item: HierarchyLevel & HierarchyDataItem,
    newCheckedValue: boolean
  ) {
    try {
      item.checked = newCheckedValue;
      if (item.facilityname) {
        if (this.includedFacilities.indexOf(item) === -1) {
          this.includedFacilities.push(item);
        } else {
          this.includedFacilities = this.includedFacilities.filter(
            (x) => x.id !== item.id
          );
        }

        if (this.listCheckedFacilities) {
          this.updateSelectedFacilityId(item.id, newCheckedValue);
        }
      }

      if (item.items) {
        item.items.forEach((facility) => {
          if (
            facility.status.toLocaleLowerCase() !==
            AppConstants.disabled.toLocaleLowerCase()
          )
            return this.walkAndCheck(facility as any, newCheckedValue);
        });

        item.items = [...item.items];
      }

      if (item.levels) {
        item.levels.forEach((facility) =>
          this.walkAndCheck(facility as any, newCheckedValue)
        );
      }

      if (newCheckedValue) {
      this.hierarchyData.hierarchies.forEach((each) => {
        this.selectParents(item.id, each);
      });
    } else {
        this.hierarchyData.hierarchies.forEach((each) => {
          this.unselectParents(item.name, each);
        });
      }

        if ([item.depth, item.levelid, item.levelname].every(v => v === null))
        {// unmapped facilities
           var isAllFacilitiesChecked = true;
          this.hierarchyData.unmappeditems.forEach((each) =>{
            if(!each.checked){
              isAllFacilitiesChecked = false;
            }     
          });
          this.unmappedChecked = isAllFacilitiesChecked;
      }
    } catch (error) {
      console.log("unhandled error:", error);
    }

    if (this.emitOnChange) {
      this.facilitiesSelected.emit(this.getAllFacilities());
    }
  }

  checkUnmappedFacilities() {
    this.unmappedChecked = !this.unmappedChecked;

    this.hierarchyData.unmappeditems.forEach(item => {
      const isActive = item.status.toLocaleLowerCase() !== AppConstants.disabled.toLocaleLowerCase();
      item.checked = this.unmappedChecked && isActive;

      if (!this.listCheckedFacilities && isActive && this.unmappedChecked) {
        this.includedFacilities.push(item);
      }

      if (this.listCheckedFacilities) {
        this.updateSelectedFacilityId(item.id, item.checked);
      }
    });
  }

  unselectParents(name: string, searchPivot: any): boolean {
  if (searchPivot.name === name) {
    return true;
  }

  if (searchPivot.levels?.length) {
    const found = searchPivot.levels.some((level: any) => this.unselectParents(name, level));
    if (found) {
      const allLevelsChecked = searchPivot.levels.every((level: any) => level.checked);
      const allItemsChecked = searchPivot.items?.every((item: any) => item.checked) ?? true;
      searchPivot.checked = allLevelsChecked && allItemsChecked;
      return true;
    }
  }

  if (searchPivot.items?.length) {
    const found = searchPivot.items.some((item: any) => this.unselectParents(name, item));
    if (found) {
      const allItemsChecked = searchPivot.items.every((item: any) => item.checked);
      const allLevelsChecked = searchPivot.levels?.every((level: any) => level.checked) ?? true;
      searchPivot.checked = allLevelsChecked && allItemsChecked;
      return true;
    }
  }
  return false;
  }

  private selectParents(childId: string, searchPivot: HierarchyLevel): boolean {
    if (searchPivot.levels?.length) {
      for (let level of searchPivot.levels) {
        if (this.selectParents(childId, level)) {
          if (this.isAllChecked(searchPivot)) {
            searchPivot.checked = true;
          }
          return true;
        }
      }
    }
    if (searchPivot.items?.some(item => item.id === childId)) {
      if (this.isAllChecked(searchPivot)) {
        searchPivot.checked = true;
      }
      return true;
    }
    return false;
  }

  private isAllChecked(node: HierarchyLevel): boolean {
    const itemsOk = node.items?.every(i => i.checked) ?? true;
    const levelsOk = node.levels?.every(l => this.isAllChecked(l)) ?? true;
    return itemsOk && levelsOk;
  }

  onAddLevel(parentLevel: HierarchyLevel | null) {
    this.handleClose(
      this.dialog.open(LevelModalComponent, {
        data: {
          mode: LevelMode.ADD,
          parentLevel,
          corpId: this.corpId,
        },
      })
    );
  }

  async onEditLevel(levelToEdit: HierarchyLevel) {
    this.handleClose(
      this.dialog.open(LevelModalComponent, {
        data: {
          mode: LevelMode.EDIT,
          currentLevel: levelToEdit,
          corpId: this.corpId,
        },
      })
    );
  }

  async onDeleteLevel(levelToDelete: HierarchyLevel) {
    this.handleClose(
      this.dialog.open(LevelModalComponent, {
        data: {
          mode: LevelMode.DELETE,
          currentLevel: levelToDelete,
          corpId: this.corpId,
          facilitiesToReAssign: this.getAllFacilitiesUnderLevel(levelToDelete),
          reassignableLevels: this.getAllLevelsWithFacilities().filter(
            (x) => x.id !== levelToDelete.id
          ),
        },
      })
    );
  }

  private handleClose(dialogRef: MatDialogRef<LevelModalComponent>) {
    dialogRef.afterClosed().subscribe(() => {
      this.hierarchyUpdated.emit();
    });
  }

  private getAllFacilitiesUnderLevel(
    level: HierarchyLevel
  ): HierarchyDataItem[] {
    let levels = [] as HierarchyDataItem[];
    if (level.levels) {
      levels.push(
        ...flatten(
          level.levels.map((subLevel) =>
            this.getAllFacilitiesUnderLevel(subLevel)
          )
        )
      );
    }

    if (level.items) {
      levels.push(...level.items);
    }

    return levels;
  }

  onRowAdded(event: any, levelToRemove: HierarchyLevel | null) {
    const levelToAdd: HierarchyLevel | null = this.findLevelById(
      +event.node.id,
      this._hierarchyData.hierarchies
    );
    if (levelToAdd) {
      levelToAdd.items = [...levelToAdd.items, event.data];
      this.facilityMoved.emit({
        facilityId: event.data.id,
        levelId: levelToAdd.id,
      });
    }

    if (levelToRemove) {
      levelToRemove.items = levelToRemove.items.filter(
        (item) => item.id !== event.data.id
      );
    } else {
      this.hierarchyData.unmappeditems =
        this.hierarchyData.unmappeditems.filter(
          (item) => item.id !== event.data.id
        );
    }
  }

  getAllFacilities() {
    return flatten(
      this.hierarchyData.hierarchies.map((level) =>
        this.getAllFacilitiesRecursive(level)
      )
    );
  }

  getAllFacilitiesRecursive(level: HierarchyLevel): any {
    let levels = [] as any;
    if (level.levels?.length) {
      levels.push(
        ...flatten(
          level.levels.map((subLevel) =>
            this.getAllFacilitiesRecursive(subLevel)
          )
        )
      );
    }

    if (level.items?.length) {
      levels.push(...level.items);
    }

    return levels;
  }

  private findLevelById(
    id: number,
    levels: HierarchyLevel[]
  ): HierarchyLevel | null {
    let foundLevel: HierarchyLevel | null = null;
    for (let i = 0; i < levels.length; i++) {
      if (levels[i].id === id) {
        foundLevel = levels[i];
        break;
      } else {
        foundLevel = this.findLevelById(id, levels[i].levels);
        if (foundLevel) {
          break;
        }
      }
    }

    return foundLevel;
  }

  getAllLevelsWithFacilities() {
    return flatten(
      this.hierarchyData.hierarchies.map((level) =>
        this.getAllLevelsWithFacilitiesRecursive(level)
      )
    );
  }
  getAllLevelsWithFacilitiesRecursive(level: HierarchyLevel): any {
    let levels = [] as any;
    if (level.levels?.length) {
      levels.push(
        ...flatten(
          level.levels.map((subLevel) =>
            this.getAllLevelsWithFacilitiesRecursive(subLevel)
          )
        )
      );
    }

    if (level.canmovein) {
      levels.push(level);
    }

    return levels;
  }

  levelTrackBy(index: number, level: HierarchyLevel) {
    return level.id;
  }

  itemTrackBy(index: number, item: HierarchyDataItem) {
    return item.id;
  }
}
